import React, { useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import BingAdsWebUIStaticLegacyPageContext from '@bingads-webui-static-legacy/page-context';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { BudgetComponent } from '@bingads-webui-campaign-react/budget-component';
import {
  BidStrategyComponent,
  MAX_CONVERSIONS,
  MAX_CONVERSIONS_VALUE
} from '@bingads-webui-campaign-react/bid-strategy-component';
import { useIsMounted } from '@bingads-webui-campaign-react/step-wizard-controls';
import { ConversionBasedBiddingBanner } from '@bingads-webui-campaign-react/wizard-shared-components';
import { TargetCpaWarningBanner } from '@bingads-webui-campaign-react/target-cpa-warning-banner';
import { ValidatedCostPerSale } from '@bingads-webui-campaign-react/cost-per-sale-option';
import { ValidatedNewCustomerAcquisition, ValidatedNewCustomerAcquisitionHeader, newCustomerAcquisitionServerErrorFilter } from '@bingads-webui-campaign-react/new-customer-acquisition';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import {
  Collapse,
  TextTitle,
  useCollapseState,
  collapseExpander,
  ValidatedPanel,
  getPanelErrorProps,
  ValidatedBootstrapPanel,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { useFormService, FORM_STATES } from '@bingads-webui-react/form';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  FormErrorGroup,
  UnmappedFormErrors as OriginalUnmappedFormErrors,
  entityIs,
  propertyIs,
  andComp,
} from '@bingads-webui-campaign-react/form-error';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import {
  apiErrorMapper,
  ERROR_ENTITY,
  CostPerSaleImg,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import {
  withUnvalidatedControlErrors,
  withControlErrorHandling,
} from '@bingads-webui-campaign-react/error-handling';
import { BudgetSuggestionContainer, MODE } from '@bingads-webui-campaign-react/budget-suggestion-v2';
import { CampaignTypes } from '@bingads-webui-campaign-react/constants';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { useWizardService, getFlowFactors } from '@bingads-webui-react/wizard';
import { BudgetStepSection, AllBudgetStepSections } from './utils/const';
import { useLocalizeBudget } from './utils/use-budget-localized';
import BidStrategyIcon from './media/bid-strategy.svg';
import BudgetIcon from './media/budget.svg';
import { getBudgetFormStyles } from './styles/get-budget-form-styles';
import { useBudgetSuggestionData } from './use-budget-suggestion-data';
import { FLOWS } from '../step-constants';
import { SingleLineShimmer } from '../../shared/components/section-shimmer';

const { BudgetRange } = BingAdsWebUIStaticLegacyPageContext;

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const budgetProp = andComp(entityIsCampaign, propertyIs('Budget'));
const bidStrategyProp = andComp(entityIsCampaign, propertyIs('BiddingScheme'));
const campaignCPSOptOutProp = andComp(entityIsCampaign, propertyIs('CostPerSaleOptOut'));

const ServerValidatedBudgetComponent = withControlErrorHandling(withUnvalidatedControlErrors(BudgetComponent, { filter: budgetProp }));
const ServerValidatedBidStrategyComponent = withControlErrorHandling(withUnvalidatedControlErrors(BidStrategyComponent, { filter: bidStrategyProp }));
const ServerValidatedCostPerSale = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedCostPerSale, { filter: campaignCPSOptOutProp }));
const ServerValidatedNewCustomerAcquisition = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedNewCustomerAcquisition, { filter: newCustomerAcquisitionServerErrorFilter }));
const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);

const BID_STRATEGY_OPTIONS = [
  MAX_CONVERSIONS,
  MAX_CONVERSIONS_VALUE,
];

export const BudgetStepShell = ({
  data: budgetInfo,
  onDataChange,
  error,
}) => {
  const {
    campaign,
    conversionGoalDetails,
    budgetAmount,
    bidStrategy,
    currency,
    cpa,
    roas,
    isInitialAdditionalDetailsChecked,
    budgetSuggestionData,
    fetchCustomBudgetPerformanceData,
    budgetTier,
    loadBudgetPartial,
  } = budgetInfo || {};
  const {
    costPerSaleOptOut,
    newCustomerAcquisition,
    newCustomerAcquisitionOptOut,
  } = campaign || {};

  const getStyles = useResponsiveStyles(getBudgetFormStyles);
  const { serverErrors, systemErrors } = error || {};
  const { state } = useFormService();
  const { saveFailed } = state.context;
  const isLoading = state.matches(FORM_STATES.LOADING);
  const isDataLoading = useMemo(() => _.isEmpty(budgetInfo), [budgetInfo]);
  const { localizeBudget } = useLocalizeBudget();
  const isMounted = useIsMounted();
  const serverContext = useContext(ServerContext);
  const bidRange = (serverContext && serverContext.KeywordBidRange) || null;
  const { Permissions, DynamicPermissions, CurrentAccount: { AdvertiserCustomerID: advertiserCustomerId, CurrencyCode } } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const {
    SupportCPSForPmax: IsSupportCPSForPmaxEnabled,
    EnableBudgetSuggestion,
    NewCustomerAcquisition: IsNewCustomerAcquisitionEnabled,
  } = permissions.dynamic;

  const activity = useContext(ActivityContext);

  const { state: wizardState } = useWizardService();

  const { flow } = getFlowFactors(wizardState);

  const shouldShowBudgetSuggestion = useMemo(() => EnableBudgetSuggestion && flow === FLOWS.PERFMAX_WIZARD, [EnableBudgetSuggestion, flow]);

  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext) || {};

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  const mergedI18n = useMemo(() => mergeI18n(
    legacyI18n,
    i18n
  ), [i18n, legacyI18n]);

  const [
    allSections,
    expandedSections,
    setExpandedSections,
  ] = useCollapseState(AllBudgetStepSections, AllBudgetStepSections);

  const expandSections = useMemo(() =>
    collapseExpander({ setActiveKeys: setExpandedSections }), [setExpandedSections]);

  const {
    isBudgetSuggestionLoading,
    customPerformanceData,
    budgetSuggestionData: updatedBudgetSuggestionData,
  } = useBudgetSuggestionData({
    loadBudgetPartial,
    shouldShowBudgetSuggestion,
    campaign,
    fetchCustomBudgetPerformanceData,
    cpa,
    roas,
    bidStrategy,
    budgetTier,
    budgetAmount,
  });

  /**
   * This is a workaround to fit the budget schema inside PMaxLite's BudgetComponent.
   * The schema expects suggestion from UP providing minimum and maximum budget which doesn't work here.
   * Using the valid range from legacy budget schema instead.
   * */
  const { DailyRange } = BudgetRange || {};

  const budgetSuggestion = useMemo(() => ({
    Currency: currency,
    GlobalMaximumBudget: { DailyBudget: DailyRange.CeilingValue ? DailyRange.CeilingValue : 3000000 },
    GlobalMinimumBudget: { DailyBudget: DailyRange.FloorValue ? DailyRange.FloorValue : 0.05 },
  }), [currency, DailyRange.CeilingValue, DailyRange.FloorValue]);

  const budgetTitleDetails = useMemo(() => i18n.getString(_TL_('{{budgetAmount}} {{currency}} per day'), { budgetAmount, currency }), [budgetAmount, currency, i18n]);

  const bidStrategyTitleDetails = useMemo(() => {
    let targetValue = '';
    if (bidStrategy === MAX_CONVERSIONS.type && cpa) {
      targetValue = `${cpa} ${currency}`;
    } else if (bidStrategy === MAX_CONVERSIONS_VALUE.type && roas) {
      targetValue = `${roas} %`;
    }
    return (
      <>
        {i18n.getString(_TL_('{{bidStrategy}}'), { bidStrategy }) + (targetValue ? `, ${i18n.getString(_TL_('target {{targetValue}}'), { targetValue })}` : '')}
        <TargetCpaWarningBanner
          budgetAmount={budgetAmount}
          cpa={cpa}
          activity={activity}
          scenario="PmaxNorthStarWizard"
        />
      </>
    );
  }, [bidStrategy, cpa, roas, i18n, budgetAmount, activity, currency]);

  const onFieldChange = useCallback((updatedData) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        ...updatedData,
      }),
    });
  }, [onDataChange]);

  const onBidStrategyChange = useCallback((e, newBidStrategy) => {
    const bidStrategyType = _.get(newBidStrategy, 'type');
    onFieldChange({ bidStrategy: bidStrategyType });
  }, [onFieldChange]);

  const onBudgetChange = useCallback((newBudget, shouldLocalize = false) => {
    const budgetAmountValue = shouldLocalize ? localizeBudget(newBudget, currency) : newBudget;
    onFieldChange({ budgetAmount: budgetAmountValue });
  }, [currency, onFieldChange, localizeBudget]);

  const onBudgetValidate = useCallback(({ isValid, messages }) => {
    onFieldChange({ isBudgetValid: isValid, budgetErrors: messages });
  }, [onFieldChange]);

  const onBudgetSuggestionValidate = useCallback((result) => {
    const { data = {}, isValid, messages } = result || {};

    onFieldChange({
      budgetTier: data.budgetTier,
      budgetAmount: `${data.budget}`,
      isBudgetValid: isValid,
      budgetErrors: messages,
    });
  }, [onFieldChange]);

  const onMarketingMetricChange = useCallback(({
    data: newMarketingMetric,
    isValid,
    messages,
  }) => {
    onFieldChange({
      ...newMarketingMetric,
      isMarketingMetricValid: isValid,
      marketingMetricErrors: messages,
    });
  }, [onFieldChange]);

  const onCampaignValidation = useCallback((results) => {
    if (!isMounted.current) return;
    onDataChange({
      data: prevData => ({
        ...prevData,
        campaign: {
          ...prevData.campaign,
          ...results.data,
        },
      }),
      isValid: results.isValid,
      isDirty: results.isDirty,
    });
  }, [isMounted, onDataChange]);

  return (
    <ActuationGroup actuationProps={{ isActive: saveFailed }}>
      <AttentionBeaconGroup>
        <div className={classNames(getStyles('formContainer'), 'budget-step-container')}>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, saveFailed, errorMapper: apiErrorMapper }}>
            <>
              <div className={classNames(getStyles('header'))}>
                <h1>{i18n.getString(_TL_('How much do you want to spend?'))}</h1>
                <span>{i18n.getString(_TL_('Tell us about your campaign and how much you want to spend'))}</span>
              </div>
              <UnmappedFormErrors className="other-server-errors" />
              <div className="system-errors">
                <ErrorMessages messages={systemErrors} />
              </div>
              <ValidationGroup
                validationProps={{
                  schema: Campaign,
                  onValidate: onCampaignValidation,
                  data: campaign,
                  i18n: mergedI18n,
                }}
              >
                <Collapse
                  checkItems
                  skipItemsTypeCheck
                  allPanelItems={allSections}
                  activeKeys={expandedSections}
                  setActiveKeys={setExpandedSections}
                >
                  <ValidatedPanel
                    key={BudgetStepSection.Budget}
                    title={
                      <TextTitle
                        icon={BudgetIcon}
                        titleKey={_TL_('Budget')}
                        topicKey="pop_BA_PMAXLite_Budget"
                        as="h2"
                        headingClassName={classNames(getStyles('budgetTitle'))}
                        details={budgetTitleDetails}
                      />
                    }
                    actuationProps={{
                      expandKey: BudgetStepSection.Budget,
                      expand: expandSections,
                    }}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  >
                    {
                      shouldShowBudgetSuggestion ? (
                        <BudgetSuggestionContainer
                          isLoading={isLoading || isBudgetSuggestionLoading}
                          data={updatedBudgetSuggestionData || budgetSuggestionData}
                          mode={MODE.CREATION}
                          currency={CurrencyCode}
                          campaignType={CampaignTypes.PerformanceMax}
                          onBudgetValidate={onBudgetSuggestionValidate}
                          customPerformanceData={customPerformanceData}
                          defaultBudget={budgetAmount}
                          bidType={bidStrategy}
                          minBudget={budgetSuggestion.GlobalMinimumBudget.DailyBudget}
                          maxBudget={budgetSuggestion.GlobalMaximumBudget.DailyBudget}
                        />
                      ) : (
                        <ServerValidatedBudgetComponent
                          showDropdown={false}
                          currency={CurrencyCode}
                          budget={budgetAmount}
                          onBudgetChange={onBudgetChange}
                          isLoading={isLoading}
                          onNotifyValidation={onBudgetValidate}
                          withSlider={false}
                          budgetSuggestion={budgetSuggestion}
                        />
                      )}
                  </ValidatedPanel>
                  {IsNewCustomerAcquisitionEnabled && (
                    <ValidatedPanel
                      key={BudgetStepSection.NewCustomerAcquisition}
                      title={
                        <ValidatedNewCustomerAcquisitionHeader
                          validationProps={{ data: { newCustomerAcquisitionOptOut } }}
                        />
                      }
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      actuationProps={{
                        expand: BudgetStepSection.NewCustomerAcquisition,
                        expandKey: expandSections,
                      }}
                    >
                      {
                        isDataLoading ? <SingleLineShimmer /> : (
                          <ServerValidatedNewCustomerAcquisition
                            validationProps={{ data: { newCustomerAcquisition } }}
                            cid={advertiserCustomerId}
                            aid={scope.values.aid}
                            bidStrategy={bidStrategy}
                            optOut={newCustomerAcquisitionOptOut}
                          />
                        )
                      }
                    </ValidatedPanel>
                  )}
                  <ValidatedPanel
                    key={BudgetStepSection.BidStrategy}
                    title={
                      <TextTitle
                        icon={BidStrategyIcon}
                        titleKey={_TL_('Bid strategy')}
                        topicKey="pop_BA_PMAX_BidStrategy"
                        as="h2"
                        headingClassName={classNames(getStyles('budgetTitle'))}
                        details={bidStrategyTitleDetails}
                      />
                    }
                    actuationProps={{
                      expandKey: BudgetStepSection.BidStrategy,
                      expand: expandSections,
                    }}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  >
                    <ServerValidatedBidStrategyComponent
                      options={BID_STRATEGY_OPTIONS}
                      selectedBidStrategy={bidStrategy || MAX_CONVERSIONS_VALUE.type}
                      onBidStrategyChange={onBidStrategyChange}
                      defaultChecked={isInitialAdditionalDetailsChecked}
                      marketingMetricsValue={{ cpa, roas }}
                      onMarketingMetricChange={onMarketingMetricChange}
                      inputUnit={CurrencyCode}
                      bidRange={bidRange}
                      isLoading={isLoading}
                      hideMessage
                    />
                    <TargetCpaWarningBanner
                      budgetAmount={budgetAmount}
                      cpa={cpa}
                      activity={activity}
                      scenario="PmaxNorthStarWizard"
                    />
                    <ConversionBasedBiddingBanner
                      i18n={i18n}
                      scope={scope}
                      campaign={campaign}
                      conversionGoalsDetails={conversionGoalDetails}
                      isCampaignCreation
                    />
                  </ValidatedPanel>
                  {
                    IsSupportCPSForPmaxEnabled && (
                      <ValidatedBootstrapPanel
                        key="advanced-bid-strategy"
                        defaultExpanded={false}
                        className={classNames(getStyles('collapsiblePanel'), 'advanced-bid-strategy-panel')}
                        render={() => (
                          <>
                            <BootstrapPanel.Heading>
                              <BootstrapPanel.Title toggle>
                                {i18n.getString(_TL_('Advanced bid strategy'))}
                              </BootstrapPanel.Title>
                            </BootstrapPanel.Heading>
                            <BootstrapPanel.Collapse>
                              <BootstrapPanel.Body>
                                <div className={classNames(getStyles('advancedBidStrategyContent'))}>
                                  <ValidatedPanel
                                    title={<TextTitle
                                      titleKey={_TL_('Cost per sale')}
                                      icon={CostPerSaleImg}
                                    />}
                                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                                    isActive
                                  >
                                    <ServerValidatedCostPerSale
                                      validationProps={{ data: costPerSaleOptOut }}
                                    />
                                  </ValidatedPanel>
                                </div>
                              </BootstrapPanel.Body>
                            </BootstrapPanel.Collapse>
                          </>
                        )}
                      />
                    )
                  }
                </Collapse>
              </ValidationGroup>
            </>
          </FormErrorGroup>
        </div>
      </AttentionBeaconGroup>
    </ActuationGroup>
  );
};

BudgetStepShell.propTypes = {
  data: PropTypes.shape({
    budgetAmount: PropTypes.number,
    bidStrategy: PropTypes.string,
    currency: PropTypes.string,
    cpa: PropTypes.number,
    roas: PropTypes.number,
    costPerSaleOptOut: PropTypes.bool,
    isInitialAdditionalDetailsChecked: PropTypes.bool,
    campaign: PropTypes.shape({}),
    conversionGoalDetails: PropTypes.shape({}),
  }).isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
};
